import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getFacilityKeyForPortal, getPatientKey, useProfileStore } from "@/store/profile";
import { useDemographicsStore } from "@/store/demographics";
import { computeAgeInDays } from "@/lib/ageCalendarDays";
import { filterWirePageRecords } from "@/lib/ageFilterWirePages";
import { useTaskStore } from "@/store/task";
import taskRequest from "@/api/task-request";
import type { CaseHistoryConfig } from "@/types/task-actions";
import { WireAnswersContext } from "@/components/case-history-wire/wireAnswersContext";
import {
  CaseHistoryWireInteractive,
  parseWireDocument,
} from "@/components/case-history-wire/CaseHistoryWireInteractive";
import { Button } from "@/components/ui/Button";
import { WizardActionsBar } from "@/components/wizard/WizardActionsBar";
import { SESSION_PORTAL_CONFIG_RESET_PENDING } from "@/constants/portalConfigSession";
import { finishHistoryStep } from "@/utils/navigateToFormHandler";
import { wizardPrevPath, GENERAL_HISTORY_CONFIG_PATH } from "@/hooks/useWizardFlow";
import {
  isApiLockedResponse,
  isPortalConfigRecordLocked,
  PORTAL_SESSION_LOCKED_PATIENT_MESSAGE,
  portalLockedMessageFromError,
} from "@/utils/portalSessionLock";
import { parseApiIdResult } from "@/utils/parseApiIdResult";

const SAVE_DEBOUNCE_MS = 900;

function errorMessageFromUnknown(e: unknown, fallback: string): string {
  if (e instanceof Error && e.message.trim()) return e.message.trim();
  return fallback;
}

/** GET may return a JSON string or a parsed object (axios). Never use String(object) — breaks JSON.parse. */
function normalizePortalConfigTemplateRaw(raw: unknown): string {
  if (raw == null) return "{}";
  if (typeof raw === "string") return raw;
  if (typeof raw === "object") return JSON.stringify(raw);
  return String(raw);
}

/** Load answers from JSON array `[{ problemCode, answer }]`. */
function parsePortalConfigAnswersJson(value: string): Record<string, unknown> {
  if (!value.trim()) return {};
  try {
    const parsed = JSON.parse(value) as unknown;
    if (!Array.isArray(parsed)) return {};
    const out: Record<string, unknown> = {};
    for (const row of parsed) {
      if (!row || typeof row !== "object" || Array.isArray(row)) continue;
      const o = row as Record<string, unknown>;
      const code = o.problemCode;
      if (typeof code === "string" && code.length > 0) {
        out[code] = o.answer;
      }
    }
    return out;
  } catch {
    /* ignore */
  }
  return {};
}

function serializePortalConfigAnswers(record: Record<string, unknown>): string {
  const entries = Object.entries(record).filter(([k]) => k.length > 0);
  entries.sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(entries.map(([problemCode, answer]) => ({ problemCode, answer })));
}

/** True if any page uses `showForAgeDays` with at least one finite min/max (same idea as the wire schema). */
function wireDocHasAgeGatedPages(doc: Record<string, unknown>): boolean {
  const list = (Array.isArray(doc.pages) ? doc.pages : Array.isArray(doc.Pages) ? doc.Pages : []) as unknown[];
  for (const p of list) {
    if (!p || typeof p !== "object" || Array.isArray(p)) continue;
    const sfa = (p as Record<string, unknown>).showForAgeDays;
    if (sfa == null || typeof sfa !== "object" || Array.isArray(sfa)) continue;
    const s = sfa as Record<string, unknown>;
    const nMin = s.min != null ? Number(s.min) : Number.NaN;
    const nMax = s.max != null ? Number(s.max) : Number.NaN;
    if (Number.isFinite(nMin) && nMin >= 0) return true;
    if (Number.isFinite(nMax) && nMax >= 0) return true;
  }
  return false;
}

export function PortalConfigPage() {
  const navigate = useNavigate();
  const facilityKey = getFacilityKeyForPortal();
  const patientKey = getPatientKey();
  const caseHistoryId = useTaskStore((s) => s.caseHistory?.id);
  const action = useTaskStore((s) => s.caseHistoryTaskAction);
  const activePortalTaskId = useTaskStore((s) => s.activePortalTaskId);
  const portalFlow = useTaskStore((s) => s.portalCaseHistoryFlow);

  // This page serves two wizard steps: the custom history (/complaint/portal-config) renders the
  // non-general configs, and the General History step (GENERAL_HISTORY_CONFIG_PATH) renders the
  // canonical TypeId === 1 config. Each route has a distinct React key so state resets between them.
  const { pathname } = useLocation();
  const isGeneralStep = pathname.startsWith(GENERAL_HISTORY_CONFIG_PATH);
  const stepPath = isGeneralStep ? GENERAL_HISTORY_CONFIG_PATH : "/complaint/portal-config";

  const configs: CaseHistoryConfig[] = useMemo(
    () =>
      (action?.caseHistoryConfigs ?? []).filter((c) =>
        isGeneralStep ? Number(c.typeId) === 1 : Number(c.typeId) !== 1
      ),
    [action, isGeneralStep]
  );

  const [configIndex, setConfigIndex] = useState(0);
  const [portalConfigId, setPortalConfigId] = useState<number | null>(null);
  const [doc, setDoc] = useState<Record<string, unknown>>({});
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [flushing, setFlushing] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipNextSave = useRef(false);
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const answersRef = useRef<Record<string, unknown>>({});

  const currentConfig = configs[configIndex] ?? null;
  const portalConfigCode = currentConfig?.portalConfigCode?.trim() ?? "";

  const setAnswer = useCallback((name: string, value: unknown) => {
    setAnswers((prev) => {
      const next = { ...prev, [name]: value };
      answersRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  const cancelPendingDebouncedSave = useCallback(() => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
  }, []);

  const flushPortalConfigAnswers = useCallback(async (): Promise<boolean> => {
    if (!facilityKey || !patientKey || !portalConfigId || portalConfigId <= 0) return true;
    cancelPendingDebouncedSave();
    const payload = serializePortalConfigAnswers(answersRef.current);
    try {
      await taskRequest.request.updateCaseHistoryPortalConfigData(
        facilityKey,
        patientKey,
        portalConfigId,
        "Answers",
        payload
      );
      return true;
    } catch (e) {
      if (isApiLockedResponse(e)) {
        setLoadError(portalLockedMessageFromError(e));
        return false;
      }
      throw e;
    }
  }, [facilityKey, patientKey, portalConfigId, cancelPendingDebouncedSave]);

  const busy = submitting || flushing;

  useEffect(() => {
    if (!portalFlow || !facilityKey || !patientKey || !caseHistoryId || caseHistoryId <= 0) {
      setLoadError("This questionnaire is not available. Return to the dashboard and open your task again.");
      setLoading(false);
      return;
    }
    if (configs.length === 0) {
      setLoadError("No portal configuration is linked to this task.");
      setLoading(false);
      return;
    }
    if (!portalConfigCode) {
      console.warn("[PortalConfigPage] caseHistoryConfig at index", configIndex, "has no portalConfigCode");
      setLoadError("No portal configuration is linked to this task.");
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      setPortalConfigId(null);
      try {
        // Load or create the CaseHistoryPortalConfig record for this config
        let portalConfigRecord = null;
        try {
          const recordRes = await taskRequest.request.getCaseHistoryPortalConfig(
            facilityKey,
            patientKey,
            caseHistoryId,
            portalConfigCode
          );
          portalConfigRecord = recordRes?.data ?? null;
        } catch {
          /* not found — will insert below */
        }

        if (isPortalConfigRecordLocked(portalConfigRecord)) {
          if (!cancelled) {
            setLoadError(PORTAL_SESSION_LOCKED_PATIENT_MESSAGE);
            setLoading(false);
          }
          return;
        }

        if (!portalConfigRecord || !portalConfigRecord.id) {
          try {
            const insertRes = await taskRequest.request.insertCaseHistoryPortalConfig(
              facilityKey,
              patientKey,
              caseHistoryId,
              portalConfigCode
            );
            const insertResult = parseApiIdResult(insertRes?.data);
            if (insertResult.locked) {
              if (!cancelled) {
                setLoadError(insertResult.error ?? PORTAL_SESSION_LOCKED_PATIENT_MESSAGE);
                setLoading(false);
              }
              return;
            }
            const insertedId = insertResult.id > 0 ? insertResult.id : null;
            if (insertedId) {
              // Re-fetch to get the full record including any existing data
              try {
                const recordRes = await taskRequest.request.getCaseHistoryPortalConfig(
                  facilityKey,
                  patientKey,
                  caseHistoryId,
                  portalConfigCode
                );
                portalConfigRecord = recordRes?.data ?? null;
              } catch {
                // Use a minimal record with the inserted id
                portalConfigRecord = { id: insertedId, typeId: 0, code: portalConfigCode, description: null, data: [] };
              }
            }
          } catch (e) {
            if (isApiLockedResponse(e)) {
              if (!cancelled) {
                setLoadError(portalLockedMessageFromError(e));
                setLoading(false);
              }
              return;
            }
            throw e;
          }
        }

        if (cancelled) return;

        const activePortalConfigId = portalConfigRecord?.id ?? null;
        if (activePortalConfigId) setPortalConfigId(activePortalConfigId);

        // Load the template
        const cfgRes = await taskRequest.request.getPortalConfigData(facilityKey, portalConfigCode);
        const text = normalizePortalConfigTemplateRaw(cfgRes?.data);
        let template: Record<string, unknown> = {};
        try {
          template = JSON.parse(text) as Record<string, unknown>;
        } catch {
          template = {};
        }

        if (cancelled) return;
        setDoc(template);
        setPageIndex(0);

        // Check reset flag (applies only on the first config)
        let resetFromDashboard = false;
        if (configIndex === 0) {
          try {
            resetFromDashboard =
              typeof sessionStorage !== "undefined" &&
              sessionStorage.getItem(SESSION_PORTAL_CONFIG_RESET_PENDING) === "1";
          } catch {
            /* ignore */
          }
        }

        // Ensure "Data" entry exists in the record
        const existingData = portalConfigRecord?.data ?? [];
        const hasDataEntry = existingData.some(
          (d) => (d.code ?? "").toLowerCase() === "data"
        );
        if (activePortalConfigId && (!hasDataEntry || resetFromDashboard)) {
          await taskRequest.request.updateCaseHistoryPortalConfigData(
            facilityKey,
            patientKey,
            activePortalConfigId,
            "Data",
            text
          );
        }

        // Load or reset answers
        let initialAnswers: Record<string, unknown> = {};

        if (resetFromDashboard) {
          // Clear answers for this config on reset
          if (activePortalConfigId) {
            try {
              await taskRequest.request.updateCaseHistoryPortalConfigData(
                facilityKey,
                patientKey,
                activePortalConfigId,
                "Answers",
                "[]"
              );
            } catch {
              /* ignore */
            }
          }
          try {
            sessionStorage.removeItem(SESSION_PORTAL_CONFIG_RESET_PENDING);
          } catch {
            /* ignore */
          }
          initialAnswers = {};
        } else {
          const answersEntry = existingData.find(
            (d) => (d.code ?? "").toLowerCase() === "answers"
          );
          if (answersEntry?.value) {
            try {
              initialAnswers = parsePortalConfigAnswersJson(answersEntry.value);
            } catch {
              /* no prior data */
            }
          }
        }

        if (cancelled) return;
        skipNextSave.current = true;
        answersRef.current = initialAnswers;
        setAnswers(initialAnswers);
      } catch (e) {
        console.error("PortalConfigPage load:", e);
        if (!cancelled) {
          setLoadError(
            errorMessageFromUnknown(
              e,
              "We could not load this questionnaire. Please try again later."
            )
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalFlow, facilityKey, patientKey, caseHistoryId, portalConfigCode, configIndex]);

  useEffect(() => {
    if (!facilityKey || !patientKey || !portalConfigId || portalConfigId <= 0 || loading) return;
    if (skipNextSave.current) {
      skipNextSave.current = false;
      return;
    }

    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const payload = serializePortalConfigAnswers(answersRef.current);
        await taskRequest.request.updateCaseHistoryPortalConfigData(
          facilityKey,
          patientKey,
          portalConfigId,
          "Answers",
          payload
        );
      } catch (e) {
        if (isApiLockedResponse(e)) {
          setLoadError(portalLockedMessageFromError(e));
        } else {
          console.error("PortalConfigAnswers autosave:", e);
        }
      }
    }, SAVE_DEBOUNCE_MS);

    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [answers, facilityKey, patientKey, portalConfigId, loading]);

  const profileDob = useProfileStore((s) => s.currentPatient?.dob);
  const demographicsDob = useDemographicsStore((s) => s.demographics?.dob);
  const fetchDemographics = useDemographicsStore((s) => s.fetchDemographics);
  const demographicsLoading = useDemographicsStore((s) => s.loading);
  const ageDobResyncTriedRef = useRef(false);
  const dobForAge = useMemo(() => {
    const t = (demographicsDob || profileDob || "").trim();
    return t.length > 0 ? t : null;
  }, [demographicsDob, profileDob]);
  const ageInDays = useMemo(
    () => computeAgeInDays(dobForAge, new Date()),
    [dobForAge]
  );

  const hasAgeGatedContent = useMemo(() => wireDocHasAgeGatedPages(doc), [doc]);

  /**
   * When the template uses age-banded pages, we must be able to compute age — otherwise show
   * an explicit error instead of hiding pages with no explanation.
   */
  const ageGatingError = useMemo((): string | null => {
    if (!hasAgeGatedContent) return null;
    if (dobForAge == null) {
      return "A date of birth is required to open this health history, but it was not found. Go back to Demographics, confirm the date of birth, save, and return. If the problem continues, return to the dashboard and open the task again.";
    }
    if (ageInDays == null) {
      return "We could not use the saved date of birth to determine age. Go back to Demographics, enter a valid date of birth, save, and return.";
    }
    return null;
  }, [hasAgeGatedContent, dobForAge, ageInDays]);

  const checkDemographicsAgain = useCallback(async () => {
    try {
      await fetchDemographics();
    } catch {
      /* ageGatingError stays; user can fix Demographics or retry */
    }
  }, [fetchDemographics]);

  // One automatic server re-sync if DOB/age is missing: fixes stale zustand persist or profile not patched yet.
  useEffect(() => {
    if (!ageGatingError) {
      ageDobResyncTriedRef.current = false;
      return;
    }
    if (ageDobResyncTriedRef.current) return;
    ageDobResyncTriedRef.current = true;
    void checkDemographicsAgain();
  }, [ageGatingError, checkDemographicsAgain]);

  /** Per-page `showForAgeDays` in wire JSON: filter to patient’s calendar age in days. */
  const displayDoc = useMemo((): Record<string, unknown> => {
    if (!hasAgeGatedContent || ageGatingError) return doc;
    const key = Array.isArray(doc.pages)
      ? "pages"
      : Array.isArray(doc.Pages)
        ? "Pages"
        : null;
    if (key == null) return doc;
    const pages = doc[key] as { showForAgeDays?: { min?: number; max?: number } }[] | undefined;
    if (!pages?.length) return doc;
    if (ageInDays == null) return doc;
    const filtered = filterWirePageRecords(
      pages as { showForAgeDays?: { min?: number; max?: number } }[],
      ageInDays
    );
    if (filtered.length === pages.length) return doc;
    return { ...doc, [key]: filtered } as Record<string, unknown>;
  }, [doc, ageInDays, hasAgeGatedContent, ageGatingError]);

  const wireDoc = useMemo(() => displayDoc, [displayDoc]);
  const parsedWire = useMemo(() => parseWireDocument(wireDoc), [wireDoc]);
  const pageCount = parsedWire.pages.length;
  const isLastPage = pageCount > 0 && pageIndex >= pageCount - 1;
  const isLastConfig = configIndex >= configs.length - 1;

  useEffect(() => {
    setPageIndex((i) => (pageCount > 0 ? Math.min(i, pageCount - 1) : 0));
  }, [pageCount, ageInDays, doc]);

  useEffect(() => {
    scrollAreaRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [pageIndex, configIndex]);

  const wizardSteps = useTaskStore((s) => s.wizardSteps);

  const handleFinish = async () => {
    if (!facilityKey || !patientKey || !caseHistoryId || !activePortalTaskId) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const saved = await flushPortalConfigAnswers();
      if (!saved) return;
      await finishHistoryStep(navigate, wizardSteps, stepPath, "/complaint/task-complete", {
        replace: true,
      });
    } catch (e) {
      if (isApiLockedResponse(e)) {
        setLoadError(portalLockedMessageFromError(e));
        return;
      }
      console.error("PortalConfigPage finish:", e);
      setSubmitError(
        errorMessageFromUnknown(e, "We could not save your responses. Please try again.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const goBack = async () => {
    setSubmitError(null);
    setFlushing(true);
    try {
      const saved = await flushPortalConfigAnswers();
      if (!saved) return;
      if (pageIndex > 0) {
        setPageIndex((i) => Math.max(0, i - 1));
        return;
      }
      if (configIndex > 0) {
        setConfigIndex((i) => i - 1);
        return;
      }
      navigate(wizardPrevPath(wizardSteps, stepPath) ?? "/complaint/primary-complaint");
    } catch (e) {
      if (isApiLockedResponse(e)) {
        setLoadError(portalLockedMessageFromError(e));
        return;
      }
      console.error("PortalConfig flush (back):", e);
      setSubmitError(
        errorMessageFromUnknown(e, "Could not save your progress. Please try again.")
      );
    } finally {
      setFlushing(false);
    }
  };

  const goContinue = async () => {
    setSubmitError(null);
    setFlushing(true);
    try {
      const saved = await flushPortalConfigAnswers();
      if (!saved) return;
      if (pageCount > 0 && !isLastPage) {
        setPageIndex((i) => Math.min(pageCount - 1, i + 1));
        return;
      }
      // Last page of current config — advance to next config
      if (!isLastConfig) {
        setConfigIndex((i) => i + 1);
        return;
      }
      // Last page of last config — finish
      await handleFinishInternal();
    } catch (e) {
      if (isApiLockedResponse(e)) {
        setLoadError(portalLockedMessageFromError(e));
        return;
      }
      console.error("PortalConfig flush (continue):", e);
      setSubmitError(
        errorMessageFromUnknown(e, "Could not save your progress. Please try again.")
      );
    } finally {
      setFlushing(false);
    }
  };

  const handleFinishInternal = async () => {
    if (!facilityKey || !patientKey || !caseHistoryId || !activePortalTaskId) return;
    await finishHistoryStep(navigate, wizardSteps, stepPath, "/complaint/task-complete", {
      replace: true,
    });
  };

  if (loadError) {
    return (
      <div className="wizard-page wizard-page--state w-full px-4 py-8">
        <p className="text-center text-sm text-red-700">{loadError}</p>
        <div className="mt-6 flex justify-center">
          <Button type="button" variant="outline" onClick={() => navigate("/")}>
            Back to dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="wizard-page wizard-page--state w-full px-4 py-12 text-center text-sm text-gray-600">
        Loading questionnaire…
      </div>
    );
  }

  if (ageGatingError) {
    return (
      <div className="wizard-page wizard-page--state w-full max-w-lg px-4 py-8">
        <p className="text-center text-sm text-red-700" role="alert">
          {ageGatingError}
        </p>
        <p className="mt-4 text-center text-sm text-gray-600">
          Demographics is required to collect date of birth before this step. If you already saved it, the app can load it
          from the server (for example if this screen opened before the client had it). You can also use Check again
          after returning from Demographics.
        </p>
        {demographicsLoading ? (
          <p className="mt-2 text-center text-sm text-gray-500" role="status">
            Loading your saved date of birth…
          </p>
        ) : null}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Button
            type="button"
            variant="primary"
            onClick={() => void checkDemographicsAgain()}
            disabled={demographicsLoading}
          >
            {demographicsLoading ? "Checking…" : "Check again"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate("/complaint/demographics")}>
            Go to Demographics
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate("/")}>
            Back to dashboard
          </Button>
        </div>
      </div>
    );
  }

  const showContinue = pageCount > 0 && (!isLastPage || !isLastConfig);
  const showFinish = pageCount === 0 || (isLastPage && isLastConfig);

  return (
    <WireAnswersContext.Provider value={{ answers, setAnswer }}>
      <div className="wizard-page flex min-h-0 w-full flex-1 flex-col overflow-hidden">
        <div
          ref={scrollAreaRef}
          className="wizard-page__content min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 sm:px-5"
        >
          {parsedWire.title ? (
            <h1 className="mb-2 text-xl font-semibold text-gray-900 sm:text-2xl">
              {parsedWire.title}
            </h1>
          ) : null}
          {pageCount > 0 ? (
            <p className="mb-6 text-sm text-gray-500" aria-live="polite">
              Page {pageIndex + 1} of {pageCount}
              {configs.length > 1 ? ` — Questionnaire ${configIndex + 1} of ${configs.length}` : null}
            </p>
          ) : null}
          <CaseHistoryWireInteractive document={wireDoc} pageIndex={pageIndex} />
          {submitError ? (
            <p className="mt-6 text-center text-sm text-red-700" role="alert">
              {submitError}
            </p>
          ) : null}
        </div>

        <WizardActionsBar>
          <Button type="button" variant="outline" onClick={() => void goBack()} disabled={busy}>
            {flushing ? "Saving…" : "Back"}
          </Button>
          {pageCount === 0 ? (
            <span className="text-xs text-gray-500">Nothing to submit.</span>
          ) : showContinue ? (
            <Button type="button" onClick={() => void goContinue()} disabled={busy}>
              {flushing ? "Saving…" : "Continue"}
            </Button>
          ) : showFinish ? (
            <Button type="button" onClick={() => void handleFinish()} disabled={busy}>
              {submitting ? "Saving…" : flushing ? "Saving…" : "Finish"}
            </Button>
          ) : null}
        </WizardActionsBar>
      </div>
    </WireAnswersContext.Provider>
  );
}
